/** English copy. Structurally identical to th.ts; `satisfies` keeps the two key sets in lock-step. */
import type th from "./th";
import type { Copy } from "./types";

const en = {
  app: {
    name: "ZPCD Portal",
    tagline: "Zoom customer monitoring for staff",
  },

  nav: {
    primary: "Main",
    home: "Home",
    dashboard: "Worklists",
    customers: "Customers",
    search: "Search",
    settings: "Settings",
    users: "Users",
    audit: "Audit log",
    pool: "Online Channel",
  },

  // Short month names for usage tables + report chart axes (keyed 1..12). Never hardcode English.
  months: {
    short: {
      "1": "Jan",
      "2": "Feb",
      "3": "Mar",
      "4": "Apr",
      "5": "May",
      "6": "Jun",
      "7": "Jul",
      "8": "Aug",
      "9": "Sep",
      "10": "Oct",
      "11": "Nov",
      "12": "Dec",
    },
  },

  // Short weekday names for the Insights report's weekly-rhythm chart (ISO weekday, 1=Mon..7=Sun).
  weekdays: {
    short: {
      "1": "Mon",
      "2": "Tue",
      "3": "Wed",
      "4": "Thu",
      "5": "Fri",
      "6": "Sat",
      "7": "Sun",
    },
  },

  action: {
    search: "Search",
    open: "Open",
    close: "Close",
    cancel: "Cancel",
    confirm: "Confirm",
    back: "Back",
    retry: "Retry",
    save: "Save",
    syncNow: "Sync data now",
    syncPlansUsage: "Sync plan & usage now",
    syncUsers: "Sync users now",
    syncAccounts: "Sync account list now",
    copyId: "Copy ID",
    sortAsc: "Sort ascending",
    sortDesc: "Sort descending",
  },

  shell: {
    omniboxPlaceholder: "Search email, account name, number, owner, or invoice no.",
    openOmnibox: "Open search",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    userMenu: "User menu",
    signOut: "Sign out",
    language: "Language",
    skipToContent: "Skip to content",
  },

  lang: {
    th: "ไทย",
    en: "English",
    switchTo: "Switch to {{lang}}",
  },

  role: {
    owner: "Owner",
    admin: "Admin",
    viewer: "Viewer",
    custom: "Custom",
  },

  freshness: {
    mirror: "Last Update: {{time}}",
    mirrorNightly: "Last Update: {{time}}",
    mirrorHealthOk: "Mirror is current",
    mirrorHealthStale: "Mirror sync is lagging",
    mirrorHealthChecked: "Checked at {{time}}",
    live: "Fetched live at {{time}}",
    unknown: "Time unknown",
    // (4) worklist matview refresh — a DISTINCT fact from mirror/mirror-health/live (never reuse a
    // timestamp across kinds). Amber variant fires when the server says the refresh is stale/errored.
    worklist: "Worklists as of {{time}}",
    worklistStale: "Worklists may be out of date — as of {{time}}",
  },

  // Utilization band labels — keyed on the five canonical band codes (display.tsx UTIL_BANDS).
  utilizationBand: {
    high: "High",
    healthy: "Healthy",
    low: "Low",
    very_low: "Very Low",
    unknown: "Unknown",
  },

  customer: {
    title: "Customer",
    plan: "Plan",
    /** The state of the plan above — see the planState block. NOT `account_status` (Zoom's ACCOUNT
     *  status, which still reads "active" on an account whose plan is long dead). */
    planStatus: "Plan status",
    seats: "Seats",
    owner: "Account owner",
    account: "Account",
    accountName: "Account name",
    accountType: "Account type",
    accountNumber: "Account no.",
    status: "Status",
    renewal: "Renewal",
    daysUntilRenewal: "in {{days}} days",
    utilization: "Utilization",
    meetings30d: "Meetings (30d)",
    tier: {
      label: "{{tier}} tier",
      unknown: "Tier unknown",
    },
    tabs: {
      overview: "Overview",
      subscription: "Subscription",
      usage: "Usage",
      users: "Users",
      reports: "Reports",
      invoice: "Invoice",
    },
    subscription: {
      basePlan: "Base plan",
      addons: "Plans",
      addon: "Add-on",
      type: "Type",
      hosts: "Hosts",
      planName: "Plan Name",
      quantity: "Quantity",
      billingPeriod: "Billing Period",
      // {{qty}} arrives formatted ("18,000" — W4-Q7b), so it is not an i18next plural count.
      licenses: "{{qty}} license(s)",
      // W4-Q7c: Zoom's counted form — phone child lines ("1 license", "124 licenses") and the
      // top-level Workspace Reservation / Contact Center Essentials lines ("7 licenses").
      licenseOne: "{{qty}} license",
      licenseMany: "{{qty}} licenses",
      // T4 (wave 4): Zoom's own units — "1 room" / "3 rooms" (KMUTT, ITOCHU), "20 ports" (Court of Justice).
      roomOne: "{{qty}} room",
      roomMany: "{{qty}} rooms",
      portOne: "{{qty}} port",
      portMany: "{{qty}} ports",
      details: "Details",
      effective: "Service Effective Date",
      nextInvoice: "Next Invoice Date",
      noAddons: "No plans",
      noAddonsBody: "This account has no subscription plans",
      billing: "Billing",
      bundled: "Bundled",
      paid: "Paid",
      tierUnknown: "Tier unknown",
      expiresOn: "(expires on {{date}})",
      freePlanTitle: "You are currently on a Basic Plan (Free)",
      freePlanBody: "Basic Plan never expires. This account has no paid subscription.",
    },
    usage: {
      recordingStorage: "Recording storage",
      planUsage: "Usage by plan",
      plan: "Plan",
      assigned: "Assigned",
      capacity: "Capacity",
      usedStorage: "Used storage",
      planStorage: "Plan storage",
      unlimited: "Unlimited",
      noPlanUsage: "No plan usage data",
      noStorage: "No recording storage data",
      idleSeats: "{{count}} idle",
      fullyUsed: "Fully used",
      payg: "Metered",
      // Owner ruling 2026-09-18 (N0-3): Zoom answered 403/2031 for this account's phone calls.
      phoneNotEnabled: "Zoom Phone is not enabled on this account yet.",
    },
    phone: {
      calls: "Calls",
      totalCalls: "Total calls",
      direction: "In / Out",
      inboundOutbound: "Inbound / Outbound",
      talkTime: "Total talk time",
      min: "min",
      approxHours: "≈ {{hours}} hours",
      activeDays: "{{count}} active days",
      inbound: "Inbound",
      outbound: "Outbound",
    },
    users: {
      email: "Email",
      type: "Type",
      firstName: "First name",
      lastName: "Last name",
      license: "License",
      role: "Role",
      lastLogin: "Last login",
      roleOwner: "Owner",
      roleAdmin: "Admin",
      roleMember: "Member",
      roleCustom: "Custom",
      licenseBasic: "Zoom Meetings Basic",
      licenseNone: "None",
      licensePhone: "Zoom Phone",
      licenseWebinar: "Zoom Webinars",
      licenseLargeMeeting: "Large Meeting",
      typeBasic: "Basic",
      typeLicensed: "Licensed",
      typeOnPrem: "On-Prem",
      typeOther: "Other",
      filterPlaceholder: "Filter by name, email or role",
      filterByLicense: "License:",
      clearLicenseFilter: "Clear",
      rosterCadenceNote:
        "The Zoom user roster is refreshed on a schedule, so this list can lag the live account by a few days. Use Sync now for up-to-the-minute data.",
      count: "Showing {{shown}} of {{total}}",
      fromSearch: "from search",
      noMatch: "No matching users",
      noMatchBody: "Nothing matched “{{term}}”",
      tabUsers: "Users ({{total}})",
      tabPending: "Pending ({{count}})",
      tabPendingNotLoaded: "Pending",
      subTabsLabel: "Users and pending invitees",
      pendingNote:
        "Invited, has not accepted yet — still belongs to their current account until they accept.",
      invited: "Invited",
      noPending: "No pending invitations",
      noPendingBody: "No pending invitations in the latest roster snapshot.",
      filterPendingPlaceholder: "Filter by email",
      noPendingMatch: "No matching pending invitations",
      noPendingMatchBody: "No pending invitation matched “{{term}}”",
      pendingNotLoaded: "The pending-invite list has not been loaded yet.",
      pendingNotLoadedBody: "It appears after the next nightly roster sync.",
      truncatedNote:
        "Showing the first {{returned}} users (of {{total}}). The counts above reflect all users; the table is capped for performance.",
    },
    reports: {
      print: "Print / PDF",
      period: "Period",
      generate: "Generate report",
      choosePrompt: "Choose a period",
      chooseBody: "Pick a date range and type, then Generate to build the report.",
      from: "From",
      to: "To",
      type: "Type",
      typeBoth: "Meetings & Webinars",
      typeMeetings: "Meetings",
      typeWebinars: "Webinars",
      kpiEvents: "Meetings & Webinars",
      kpiMeetings: "Meetings",
      kpiWebinars: "Webinars",
      kpiParticipants: "Participants",
      kpiMinutes: "Minutes",
      approxHours: "≈ {{hours}} hours",
      trendEvents: "{{unit}} per period",
      trendParticipants: "Participants per period",
      trendMinutes: "Minutes per period",
      kpiActiveHosts: "Active hosts",
      activeHostsHint: "Distinct hosts in the whole period — not the sum of the bars",
      trendActiveHosts: "Active hosts per period",
      activeHostsNoHistory: "No host history is available yet, so Active hosts has no data.",
      activeHostsBeforeCoverage:
        "Host data starts {{from}} — this range lies entirely before it, so Active hosts has no data.",
      activeHostsPartialCoverage:
        "Host data starts {{from}} — bars before that show no data, and the Active hosts total covers {{from}} – {{end}} only.",
      activeHostsPartialBuckets:
        "Periods marked * have incomplete host history (fewer history events than the meeting totals, or the period starts before host data), so their Active hosts count can read low.",
      heatmapMeetings: "Activity heatmap — meetings",
      heatmapMinutes: "Activity heatmap — minutes",
      noHeatmap: "No daily activity in this range",
      less: "Less",
      more: "More",
      perUser: "Usage per user",
      user: "User",
      coverageNote:
        "Per-user detail covers ~{{pct}}% of the meetings in this range — host-level history is backfilled less deeply than the headline totals, so the per-user rows can read lower.",
      noUsers: "No per-user activity in this range",
      eyebrow: "Zoom Usage Analysis · Sub-account Report",
      accountNumber: "Account no. {{number}}",
      footer: "Zoom usage report · {{account}} ({{number}}) · {{period}} · generated {{today}}",
      thinTitle: "Not enough data for a report",
      emptyRange: "No meeting or webinar activity in the selected range.",
      phoneTitle: "Zoom Phone",
      phoneTrend: "Calls per period",
      phoneNoHistory: "No call data is available yet, so Zoom Phone has no data.",
      phoneBeforeCoverage:
        "Call data starts {{from}} — this range lies entirely before it, so Zoom Phone has no data.",
      phonePartialCoverage:
        "Call data starts {{from}} — bars before that show no data, and the Zoom Phone totals cover {{from}} – {{end}} only.",
      phonePartialBuckets:
        "Periods marked * start before call data, so their counts cover part of the period only.",
      phoneAllTypes: "Zoom Phone calls are shown whatever the Type filter.",
    },
    // The SECOND Reports-tab report ("Insights", alongside "reports"/Standard above). No phone section
    // (removed 2026-09-19 — the Standard report's Zoom Phone section is the only phone view).
    reportsInsights: {
      kindLabel: "Report type",
      kindStandard: "Standard",
      kindInsights: "Insights",
      licenseTitle: "License seat utilization",
      licenseTrendTitle: "{{plan}} plan — utilization trend",
      licenseKpiCurrent: "Current utilization",
      licenseKpiAsOf: "as of {{date}}",
      licenseOtherPlansNote:
        "This account also has {{count}} other licensed plan(s) — see the Usage tab for the full breakdown.",
      licenseEmpty: "No license-usage snapshots in this range",
      sizeTitle: "{{unit}} size distribution",
      sizeCaption: "Number of {{unit}} by attendee count",
      sizeUnitAttendees: "Attendees",
      sizeKpiAvg: "Avg. attendees per event",
      sizeKpiTop: "Most common size",
      sizeEmpty: "No meetings or webinars with attendee data in this range",
      coverageNote:
        "This chart covers ~{{pct}}% of the events in the headline count — host-level history is backfilled less deeply than the daily totals, so it can read lower.",
      weeklyTitle: "Weekly activity rhythm",
      weeklyByWeekdayCaption: "{{unit}} by day of week",
      weeklyByHourCaption: "{{unit}} by hour of day (Bangkok time)",
      weeklyEmpty: "No meetings or webinars in this range",
    },
    invoices: {
      number: "Invoice no.",
      poNumber: "PO no.",
      searchBy: "Search by",
      searchInvoice: "Invoice Number",
      searchPo: "PO Number",
      searchPlaceholder: "Invoice / PO number",
      reset: "Reset",
      noMatch: "No matching invoices",
      noMatchBody:
        "No invoice matched the filters. Search is exact — check the full number, or widen the date range.",
      date: "Date",
      due: "Due",
      dueDate: "Due date",
      amount: "Amount",
      tax: "Tax",
      balance: "Balance",
      source: "Source",
      source_api: "Recent",
      source_pdf: "Archive",
      source_unknown: "—",
      pdf: "PDF",
      hasPdf: "PDF available",
      empty: "No invoices",
      emptyBody: "There are no invoices for this account yet",
      detailTitle: "Invoice {{number}}",
      lineItems: "Line items",
      charge: "Charge",
      chargeType: "Type",
      qty: "Qty",
      unitPrice: "Unit price",
      lineTotal: "Total",
      noItems: "No line items on this invoice",
      viewPdf: "View PDF",
      downloadPdf: "Download PDF",
      pdfUnavailable: "PDF not available for this invoice",
      pdfError: "Couldn't load the PDF",
    },
    money: {
      billed12m: "Billed (12 months)",
      renewalAmount: "Renewal amount",
      invoices: "Invoices",
      hidden: "You do not have access to financial data",
    },
  },

  customers: {
    searchPlaceholder: "Search customers by name, account number, or owner",
    idleTitle: "Find a customer",
    idleBody: "Type at least {{min}} characters — name, account number, or owner email.",
    empty: "No customers found",
  },

  worklist: {
    subtitle: "Accounts the team needs to act on, grouped by urgency",
    // Health-band group captions (DashboardPage) — splits the 7 tiles into two labelled groups of ≤4 so
    // the strip reads as two questions ("what needs action" vs "what's worth a look"), not one flat wall.
    band: {
      actNow: "Act now",
      review: "Review",
    },
    renewal: "Upcoming renewals",
    churn: "Churn risk",
    upsell: "License discrepancy",
    underutilized: "Underutilized",
    // "…ending SOON" — the word is load-bearing now that "Lapsed trials (never bought)" sits on the same
    // dashboard. This list is people STILL ON a trial (go close the sale); that one is people whose trial
    // is already OVER. Two words apart, two completely different motions.
    trial_expiring: "Trials ending soon",
    empty: "Nothing here",
    emptyBody: "No accounts in this list yet",
    col: {
      account: "Account",
      daysUntilRenewal: "Days left",
      trialEnds: "Trial ends",
      overdue: "{{days}} days overdue",
    },
    addonWaste: {
      title: "Add-on seat waste",
      addon: "Add-on",
      purchased: "Purchased",
      assigned: "Assigned",
      waste: "Idle seats",
      emptyBody: "No accounts have idle add-on seats",
      cat: {
        webinar: "Webinar",
        large_meeting: "Large meeting",
        phone: "Zoom Phone",
      },
    },
    // ── Two dangerously similar-sounding lists. The copy has to EARN each one. ──────────────────
    //   "Trials ending soon"  (62) = still ON a trial, not yet over  → go close the sale
    //   "Lapsed trials"       (50) = trial is over, never bought     → a PROSPECT list, a first sale
    lapsedTrial: {
      title: "Lapsed trials (never bought)",
      // Tone = OPPORTUNITY, not fault. These people tried Zoom and did not buy. Nothing is broken.
      // ONE LINE: what the list is + what to do. The rest of the reasoning lives in this comment, which is
      // where it belongs — not on the screen of the person working the list.
      note: "Trials that ended without a purchase — they have never paid us. Call them for a first sale.",
      seatsTrialled: "Seats trialled",
      lapsedOn: "Trial ended",
      daysSince: "{{days}} days ago",
      emptyTitle: "No lapsed trials",
      emptyBody: "No account has an expired trial without a purchase.",
    },
  },

  // Accounts Zoom no longer lists. BADGED everywhere a human explicitly asked for the account (search,
  // Customer-360, and the renewal / churn / trial worklists) — never hidden. Hiding would delete the one
  // fact the CSM most needs. Only the pure sell-more lists filter them out, and that happens in SQL.
  retired: {
    badge: "No longer in Zoom's account list",
    badgeSince: "No longer in Zoom's account list (since {{date}})",
    badgeShort: "Not in Zoom's list",
    // The badge says DELISTED. These say what that MEANS for every number next to it: ZPCD stops
    // refreshing an account the moment it drops off Zoom's list, so the KPIs, renewal countdowns and
    // amounts are pre-retirement snapshots that will never update again. Without this the tiles render
    // with normal healthy styling and get quoted as live data in a QBR for a non-customer.
    //
    // ⚠️ CUT TO ONE LINE, AND THIS ONE HAD TO BE. It renders in OverviewTab DIRECTLY ABOVE PlanFreezeNotice
    // (OverviewTab.tsx:132-143), and 6 accounts on prod are BOTH retired AND paid_expired — the most
    // valuable pages in the portal, a paying customer Zoom expired who has also dropped off Zoom's list.
    // On exactly those 6 pages a CSM used to read a 174-char paragraph and then a one-line notice saying
    // the same thing, with the long useless one FIRST and the short actionable one ("Verify with Zoom")
    // underneath it. That is the noise-buries-the-signal failure this whole branch exists to fix,
    // reintroduced by the half that had not been cut. The date IS the fact; the two sentences after it
    // were the lecture.
    frozen: "Figures frozen — this account has left Zoom's account list.",
    frozenSince: "Figures frozen {{date}} — the day this account left Zoom's account list.",
    frozenCell: "Frozen figure — this account has left Zoom's account list",
  },

  // ── plan_state — "Zoom says X" is not the same fact as "X is true" ──────────────────────────────
  // Zoom grants Trials on any account; when one lapses the account silently drops to Basic and
  // /plans/usage goes quiet, so the seat/licence figures in the mirror freeze forever. The portal used to
  // render them as current.
  //
  // ⚠️ THE TRAP: status='expired' is a CLAIM FROM ZOOM, NOT A FACT. QUALICA (THAILAND) has paid us for
  // four straight years and flipped active→expired overnight, with an OPEN ZOOM SUPPORT CASE saying the
  // expiry is wrong. If this copy reads like a tombstone, a CSM stops calling a paying customer — a worse
  // outcome than the bug being fixed. The RED is for URGENCY; the WORDS say "verify", never "dead".
  planState: {
    // Only two things live here now: the frozen-seat label, and the Usage-tab freeze notices below. The
    // seven plan-state BADGES were collapsed into the four-value account status (accountStatus.* /
    // lib/accountStatus.ts) on the principal's instruction — those essay-length badges (⚠️ Zoom says
    // expired…, ⚠️ Open incident…) were the clutter this cleanup removed. isPlanNotLive still drives
    // whether a freeze notice renders, and the frozen seat count is still labelled for what it is.
    seatsLastKnown: "Seats (last known)",

    // ── The Usage-tab freeze notice ────────────────────────────────────────────────────────────────
    // When the base plan is not live, Zoom's /plans/usage endpoint returns NOTHING for the account, so
    // every figure on that tab is frozen at the moment the trial or the paid term ended and can never
    // refresh. Rendering "15 purchased / 1 used" unlabelled is the bug.
    //
    // ONE LINE EACH: what happened, and what to do about it if there is anything to do. `paid_expired` is
    // the only one of the four with an action ("verify"), because it is the only one where our own evidence
    // contradicts Zoom. The other three are pure statements of fact and end there.
    //
    // ⚠️ `age` ("Zoom last refreshed this plan {{days}} days ago") IS DELETED. It was appended to a sentence
    // that says the numbers will never update again — so on the real prod rows it rendered as "…not current.
    // Zoom last refreshed this plan 0 days ago", which is both a useless sentence and a direct contradiction
    // of the one before it. A notice that argues with itself is a notice nobody believes.
    freeze: {
      lapsedTrial: "Trial expired — the figures below are the last known, not current.",
      paidExpired:
        "⚠️ Zoom says expired, but this account has paid us — the figures below are the last known, not current. Verify with Zoom.",
      expiredOther: "Plan expired — the figures below are the last known, not current.",
      cancelled: "Plan cancelled — the figures below are the last known, not current.",
      // ── round 3, ruling 2 — the account BADGES Active while plan_state alone says not-live ─────────
      // (≈39 accounts on the local mirror). The figures below are STILL last-known — nothing about that
      // changes — only the false "the plan has ended" clause does. Two variants, tone always warn, NEVER
      // the paid_expired danger tone: the account IS Active in Zoom under the 2026-09-17 ruling.
      activePhoneOnly:
        "No meeting plan on this account — Zoom Phone only. The meeting figures below are the last known, not current.",
      activeNotLive:
        "Zoom still lists an active line on this account — the figures below are the last known, not current.",
    },
  },

  adminUsers: {
    title: "Users",
    subtitle: "Staff who can sign in to the portal — their role and access",
    colUser: "User",
    colRole: "Role",
    colStatus: "Status",
    colLastLogin: "Last login",
    colLastSeen: "Last seen",
    colActions: "Actions",
    status: {
      active: "Active",
      disabled: "Disabled",
      invited: "Invited",
      not_invited: "Not invited",
      off_roster: "Off roster",
      revoked: "Revoked",
    },
    statusHelp: {
      invited: "Invited — an account was created; waiting for them to sign in the first time.",
      not_invited: "On the staff roster but not invited yet — send them an invite to grant access.",
      off_roster: "Has an account but was removed from the staff roster — sign-in is blocked",
      disabled: "Access is turned off (kill-switch)",
      revoked:
        "An admin removed them from the portal. They are a plain Zoom-roster entry again — only an Invite can let them back in.",
    },
    changeRole: "Change role",
    enable: "Enable access",
    disable: "Disable access",
    roleDialogTitle: "Change role",
    roleDialogBody:
      "Owner & Admin see everything including money. Viewer has no money access. Custom is a curated set of permissions.",
    roleLabel: "Role",
    customPermsLabel: "Custom permissions",
    customPermsNote: "Saving replaces this user's entire permission set.",
    roleUpdated: "Role updated",
    accessUpdated: "Access updated",
    disableDialogTitle: "Disable access?",
    disableDialogBody:
      "{{email}} will be signed out and blocked on their next request. You can re-enable anytime.",
    enableDialogTitle: "Enable access?",
    enableDialogBody: "{{email}} will regain access on their next sign-in.",
    save: "Save",
    never: "Never",
    errorTitle: "Couldn't apply the change",
    empty: "No portal users",
    emptyBody: "No staff have portal access yet.",
    tabPortal: "Portal users",
    tabRoster: "Zoom roster",
    portalHint: "Staff who have signed in to the portal.",
    rosterHint: "Staff synced from Zoom who haven't signed in yet — invite them to join.",
    colInvited: "Invite status",
    notInvited: "Not invited",
    invitedAt: "Invited {{time}}",
    invite: "Invite",
    reinvite: "Re-invite",
    sendInvite: "Send invite",
    inviteDialogTitle: "Send portal invite?",
    inviteDialogBody:
      "A portal invite email will be sent to {{email}}. They sign in with their 1TOALL email, then a 6-digit code emailed to them — no password.",
    inviteSent: "Invite sent to {{email}}",
    inviteRecorded: "Invite recorded",
    inviteEmailFailed:
      "The email couldn't be sent (SMTP not configured) — the invite was still recorded.",
    cancelInvite: "Cancel invite",
    keepInvite: "Keep invite",
    inviteCancelled: "Invite cancelled",
    cancelInviteDialogTitle: "Cancel this invite?",
    // The old copy said cancelling "does not block them from signing in while they remain on the staff
    // roster". That stopped being true the day the portal went invite-only: the invite CREATES the
    // account, so cancelling it DESTROYS that account and they cannot sign in at all. Say what it does.
    cancelInviteDialogBody:
      "This withdraws the pending invite for {{email}} and deletes the account it created. They go back to being a plain Zoom-roster entry and cannot sign in — pressing Invite is the only way to let them in.",
    searchPlaceholder: "Search name or email",
    rosterEmpty: "No pending invites",
    rosterEmptyBody: "Everyone on the Zoom staff roster has signed in.",

    // --- Remove a user from the portal (only ever an already-disabled account) ---
    delete: "Remove",
    deleteDialogTitle: "Remove from the portal?",
    deleteDialogBody:
      "{{email}} will be removed from the Portal user list. Their role and permissions are deleted with them.",
    deleteDialogConsequence:
      "They go back to being just a Zoom-roster entry and cannot sign in again until an admin invites them — pressing Invite is the only way to bring them back.",
    deleteConfirm: "Remove from portal",
    deleted: "Removed {{email}} from the portal",
    deletedBody: "They are back on the Zoom roster. Invite them when you want them to return.",
    deleteBlockedTitle: "Can't remove this user",
    deleteBlocked: {
      self: "You cannot remove your own account ({{email}}).",
      selfFix: "Ask another Owner to do it.",
      ownerNeedsOwner: "Only an Owner can remove an Owner — and {{email}} is an Owner.",
      ownerNeedsOwnerFix: "Ask an Owner to remove them, or change their role first.",
      stillActive:
        "{{email}} still has access. Disable them first — removing only ever deletes an account that is already disabled.",
      stillActiveFix:
        'Press "Disable access", then remove them. Two deliberate steps, on purpose — a mis-click cannot destroy an account.',
      lastOwner:
        "{{email}} is the portal's last Owner. Removing them would leave nobody who can administer it.",
      lastOwnerFix:
        "Make someone else an Owner first — right now this disabled Owner account is the only way back in.",
    },
    revokedAt: "Revoked {{time}} by {{email}}",
    inviteBack: "Invite back",
    inviteRestored: "Their portal access had been revoked — this invite restores it.",

    // --- The "Sync users now" button (the roster also syncs itself daily at 02:30) ---
    rosterSync: {
      button: "Sync users now",
      autoNote: "The roster also refreshes automatically every day at 02:30 (Bangkok).",
      lastSynced: "Roster last synced {{time}}",
      failedTitle: "The roster was not synced",
      rosterIntact: "The existing roster still stands — {{n}} staff, nobody lost access.",
      result: {
        ok: "Synced {{n}} staff from Zoom.",
        throttled: "Just synced — try again in {{seconds}}s.",
        alreadyRunning: "A roster sync is already running.",
        zoomError: "The Zoom call failed — the roster was NOT synced.",
        empty: "Zoom returned 0 staff — we refuse to trust that, so the roster was NOT synced.",
        suspicious:
          "Zoom sent back a list so short it would have removed {{n}} people from the portal — we refuse to trust it, so nothing was changed. Check Zoom, then try again.",
        notConfigured: "The staff Zoom app is not configured on this deployment.",
        unknown: "The sync returned an outcome the portal does not recognise — treat it as failed",
      },
    },

    // Total over `Permission` (api/types.ts) — see the note in th.ts. A missing label renders a raw key.
    permLabel: {
      "search:read": "Search",
      "customer:read": "Customers",
      "money:read": "Money / invoices",
      "worklist:read": "Worklists",
      "audit:read": "Audit log",
      "user:manage": "Manage users",
      "config:manage": "Manage settings",
    },
  },

  activity: {
    title: "Audit log",
    subtitle: "Sign-ins and data access across the portal",
    filterKind: "Event type",
    filterActor: "User email",
    actorPlaceholder: "exact email…",
    from: "From",
    to: "To",
    clear: "Clear",
    allKinds: "All events",
    kind: {
      login_success: "Successful login",
      login_failed: "Failed login",
      logout: "Logout",
      data_access: "Data access",
      money_access: "Money access",
      admin_action: "Admin action",
      sync_now: "Sync now",
    },
    colTime: "Time",
    colActor: "User",
    colEvent: "Event",
    colTarget: "Target / detail",
    colIp: "IP",
    loadMore: "Load more",
    end: "End of results",
    empty: "No activity",
    emptyBody: "No events match these filters.",
    account: "Account {{id}}",
    roleChange: "{{from}} → {{to}}",
  },

  home: {
    greeting: "Welcome",
    subtitle: "{{role}} · {{email}}",
    search: {
      title: "Search customers, members, or invoices",
      body: "Type an email, account name, number, owner, or invoice no. — ⌘K on any page",
    },
    worklist: {
      heading: "Today's worklists",
      viewAll: "View all",
    },
    quick: {
      heading: "Quick access",
      search: {
        title: "Find a customer",
        body: "Search by email, account name, number, owner, or invoice no. (⌘K on any page)",
      },
      customers: {
        title: "Open a customer",
        body: "See overview, subscription, usage, users, and invoices for any account",
      },
      worklists: {
        title: "Worklists",
        body: "Upcoming renewals, churn risk, upsell, and underutilized accounts",
      },
    },
  },

  search: {
    omniboxLabel: "Search across the portal",
    inputLabel: "Search customers, members, and invoices",
    resultsLabel: "Search results",
    idleTitle: "Type to search",
    idleBody:
      "Type at least {{min}} characters — email, account name, number, owner, or invoice no.",
    emptyTitle: "No results",
    emptyBody: "Nothing matched “{{term}}”",
    noPermission: "Your account does not have permission to search",
    groups: {
      members: "Members",
      accounts: "Customer accounts",
      invoices: "Invoices",
    },
    drillHint: {
      member: "Open Users tab",
      pending: "Open Pending tab",
      account: "Open customer",
      invoice: "Open Invoices tab",
    },
    // K2-A: shown on an invoice hit with no known customer (account_id null) — not clickable.
    unattributedInvoice: "Unattributed",
    // R7 (final review): a reseller-master (e.g. 941855) invoice hit — distinct from a genuinely
    // unattributed one, matching ZPCD's Excel label "1TOALL master (941855)" for the same invoice.
    masterAccountInvoice: "1TOALL master (941855)",
    // A pending invitee's row: the account that invited them. {{account}} = name · number.
    pendingInvite: "Pending invite · {{account}}",
  },

  syncNow: {
    title: "Sync data now",
    help: "Fetch the latest plan & usage straight from Zoom right now (uses quota). The overview above comes from the nightly sync.",
    lastSynced: "Last synced {{time}}",
    success: "Live data fetched",
    deduped: "Duplicate of the previous request — collapsed into one",
    apiCalls: "Used {{count}} quota call(s)",
    quota: {
      title: "Fetch quota (Heavy/day)",
      onDemandRemaining: "On-demand remaining",
      capRemaining: "Total remaining",
      used: "Used {{used}} of {{total}}",
      usedLabel: "Used",
      reserve: "Reserved for nightly sync: {{reserve}}",
    },
    blocked: {
      quota: "On-demand fetch quota is exhausted; the remainder is reserved for the nightly sync.",
      throttle: "Requests are coming too fast. Please wait a moment and retry.",
      dailySync:
        "ZPCD is running its daily data update right now — Sync now is paused for a moment so it doesn't compete for Zoom's shared quota. Please try again shortly.",
      error: "Could not fetch data from Zoom.",
      generic: "Live fetch failed.",
    },
    reason: {
      on_demand_budget_exhausted: "Daily on-demand quota exhausted",
      // The absolute-cap guard (quota.py would_block): on-demand may have headroom but the COMBINED
      // nightly + sync-now spend would eat the nightly reserve. Distinct from on_demand_budget_exhausted.
      daily_cap_would_starve_nightly:
        "Daily Heavy quota is exhausted; the rest is reserved for the nightly sync",
      per_second_rate_limit: "Per-second rate limit exceeded",
      per_operator_daily_cap_reached: "You have hit your daily fetch cap",
      // PRIMARY daily-sync gate reason (backend emits this while the ZPCD nightly/daily Heavy batch is
      // mid-flight). Must map to its own copy, else the reason sub-line falls back to the contradictory
      // generic "Live fetch failed." under the (reassuring) daily-sync title.
      zpcd_daily_sync_in_progress: "Daily sync is running now — paused briefly",
      // Schedule couldn't be read, so sync-now paused conservatively — shares the daily-sync block copy.
      peak_window_blind_fallback: "Daily sync may be running — paused to be safe",
      duplicate_within_dedupe_window: "Duplicate of the previous request",
      syncnow_zoom_app_not_configured: "Sync-now Zoom app is not configured",
      // Zoom HTTP errors arrive as "zoom_error:<code>" (the colon resolves as a flat leaf key because
      // nsSeparator is disabled). Common codes get specific copy; the rest fall back to blocked.error.
      "zoom_error:429": "Zoom rate-limited the request. Please retry shortly.",
      "zoom_error:500": "Zoom had a temporary error. Please retry.",
      "zoom_error:502": "Could not reach Zoom right now. Please retry.",
      "zoom_error:503": "Zoom is temporarily unavailable. Please try again later.",
    },
    // Auth/transport failures (not a Zoom envelope) — must steer to re-auth, NOT retry-against-Zoom.
    error: {
      authExpired: "Your session has expired or you lack permission. Please sign in again.",
    },
  },

  state: {
    loading: "Loading…",
    // aria-label for the inline spinner that stands in for a not-yet-loaded number (stat tiles, KPI
    // counts) — deliberately no ellipsis: it is announced once by a screen reader, not read as prose.
    loadingValue: "Loading",
    emptyTitle: "No data",
    // r1 critique (24/7): the old copy left an operator unable to tell "genuinely empty" from "quietly
    // broken and just not saying so". This says which one it probably is and gives ONE next step.
    emptyBody: "Nothing here right now — refresh if you were expecting data.",
    errorTitle: "Something went wrong",
    errorBody:
      "Could not load this — may be temporary. Try again, or refresh if it keeps happening.",
    notFoundTitle: "Not found",
    forbiddenTitle: "Access denied",
    forbiddenBody: "Your account does not have access to this section",
    networkError: "Could not reach the server",
    requestId: "Request ID: {{id}}",
  },

  auth: {
    signingIn: "Signing in…",
    signInRequired: "Please sign in",
    signIn: "Sign in",
    notStaff:
      "This account is not on the staff roster. Contact an administrator, or sign in with another account.",
    notStaffTitle: "Access denied",
    signInAnother: "Sign in with another account",
    signOut: "Sign out",
    staffOnly: "For 1TOALL staff only",
    loadingProfile: "Loading your profile…",
    sessionExpiredTitle: "Session expired",
    sessionExpiredBody: "Your sign-in has expired. Please sign in again to continue.",
    sessionConfigTitle: "Sign-in problem",
    sessionConfigBody:
      "We couldn't complete sign-in. This may be a configuration issue — please contact the portal administrator.",
  },

  acceptInvite: {
    title: "Accept your invite",
    validating: "Checking your invite…",
    validBody: "You've been invited to the portal. Sign in as {{email}} to get started.",
    signIn: "Sign in",
    invalidTitle: "This invite isn't valid",
    invalidBody:
      "This invite link has expired or already been used. Ask an admin to send you a new one.",
    missingToken: "This invite link is incomplete. Ask an admin to send you a new one.",
  },

  // The account's plan status, collapsed to four values for the monitoring UI (lib/accountStatus.ts).
  // A separate block from `status` below (which labels Zoom's raw account/invoice/subscription tokens):
  // these are the four the portal DERIVES and shows on the account badge.
  accountStatus: {
    active: "Active",
    free: "Free",
    expired: "Expired",
    unknown: "Unknown",
    unknownHint: "Plan code not yet identified — no action needed",
    // round 3, ruling 3: a DIFFERENT cause of the same "Unknown" word — the server itself has no plan data
    // for this account (not Free, zero renderable lines), not merely an opaque code we haven't mapped yet.
    noPlanDataHint: "No plan data for this account yet",
  },

  // Owner sign-in badge (ADR ACTIVATION-BADGE §Display, lib/activationStatus.ts). Owner decision 14/9 night:
  // three texts only, in English and IDENTICAL in th.ts (no translation). Never red or amber, never a
  // negative claim — "Pending" means "watching for the owner's first sign-in" and "Unknown"
  // means "no evidence"; neither is a claim about the customer. A test pins th.activation === en.activation.
  activation: {
    state: {
      activated: "Activate: Activated",
      pending: "Activate: Pending",
      unknown: "Activate: Unknown",
    },
    hint: {
      activated: "The owner account has signed in",
      pending: "Watching for the owner's first sign-in",
      unknown: "Owner sign-in status is unknown",
    },
    checkedAt: "Checked {{time}}",
    notUpdating: "Not updating",
    notUpdatingSince: "Not updating · last checked {{time}}",
  },

  status: {
    active: "Active",
    inactive: "Inactive",
    paid: "Paid",
    pending: "Pending",
    overdue: "Overdue",
    // Statuses that statusTone() already colours but were missing copy → previously rendered raw DB value.
    expiring: "Expiring",
    expired: "Expired",
    cancelled: "Cancelled",
    suspended: "Suspended",
    trial: "Trial",
    // Invoice statuses (Zoom billing) — were rendering the raw DB label before.
    collected: "Collected",
    credit: "Credit",
    open: "Open",
    sent: "Sent",
    void: "Void",
    posted: "Posted",
    draft: "Draft",
  },

  daterange: {
    label: "Select a date range",
    from: "From",
    to: "To",
    invalid: "Enter a valid date (DD/MM/YYYY).",
    order: "Start date must be on or before the end date.",
    adjusted: "Adjusted to the allowed date range.",
  },

  // 31/7 — DatePicker (single-date), built from DateRangePicker's shared internals (dateFieldShared.tsx).
  // Own copy rather than reusing daterange.* wholesale: a single date has no "From"/"To" pair and no
  // start-before-end ordering error, so daterange.order has no single-date counterpart here.
  datepicker: {
    label: "Select a date",
    placeholder: "Select a date",
    invalid: "Enter a valid date (DD/MM/YYYY).",
    // (31/7 พ.2) `adjusted` removed — dead since the SEAM-3 fix switched out-of-range from
    // clamp-and-commit to reject: only `outOfRange` below can ever render now.
    // Typed date parses fine but falls outside min/max — rejected, not silently clamped (SEAM-3):
    // the operator's typed text stays as-is and nothing is committed until they fix it.
    outOfRange: "That date is outside the allowed range.",
  },

  table: {
    results: "{{count}} result(s)",
    showing: "Showing {{from}}–{{to}} of {{total}}",
    perPage: "Rows per page",
    perPageN: "{{count}} per page",
    pagination: "Pagination",
    prevPage: "Previous page",
    nextPage: "Next page",
    goToPage: "Go to page {{n}}",
  },

  notFound: {
    title: "Page not found",
    body: "That page doesn't exist or may have moved.",
    home: "Go to Home",
  },

  a11y: {
    sortedBy: "Sorted by {{column}}",
    closeDialog: "Close dialog",
    notification: "Notification",
  },

  pool: {
    notOperatorTitle: "Not an Online Channel operator yet",
    notOperatorBody:
      "Your account is not on the Online Channel allow-list — ask an Owner to add you on the Online Channel Settings screen.",
    readOnlyNote: "You're a read-only Online Channel operator — you can't make changes here.",

    tabs: {
      dashboard: "Dashboard",
      accounts: "Accounts",
      users: "Users",
      intents: "Pending intents",
      reports: "Reports",
      settings: "Settings",
      system: "System",
      audit: "Audit log",
    },

    settings: {
      title: "Online Channel settings",
      subtitle:
        "Choose which accounts are in the Online Channel and manage the ones already set up",

      addAccountTitle: "Add an account",
      addAccountButton: "Add Account",
      chooseTypePrompt: "How would you like to add the account?",
      modeSubAccount: "From existing account",
      modeSubAccountDesc: "Search a sub-account already in the ZPCD mirror",
      modeCredential: "From Zoom credentials",
      modeCredentialDesc: "Enter a credential for a standalone account",

      searchTitle: "Add an account",
      searchPlaceholder: "Search by account name or number",
      searchLabel: "Search accounts to add",
      searchHint: "Type at least 2 characters",
      colAccountName: "Account name",
      colAccountNumber: "Account no.",
      colAccountType: "Account type",
      colSeats: "Seats",
      colActions: "Actions",
      alreadyPool: "Already added",
      addButton: "Add",
      addSuccess: "Added {{account}} to the Online Channel",
      addConflict: "This account is already added",
      addNotFound: "Account not found in the ZPCD mirror",
      candidatesEmptyTitle: "No accounts found",
      candidatesEmptyBody: "Try a different name or account number",

      credHelperText:
        "The Server-to-Server OAuth app on this account needs EXACTLY the scopes listed below — do " +
        'NOT tick Zoom\'s "Optional" checkbox on the scope screen, or every call will 403.',
      credScopesTitle: "Required scopes for the Server-to-Server OAuth app",
      credScopeCopyLabel: "Copy {{scope}}",
      credScopeGroupRead: "read — used today",
      credScopeGroupWrite: "write — for license removal later",
      credAccountNameLabel: "Account name",
      credAccountNumberLabel: "Account number",
      credZoomAccountIdLabel: "Zoom Account ID",
      credClientIdLabel: "Client ID",
      credClientSecretLabel: "Client Secret",
      credSecretNote: "Encrypted on save — never shown again after this.",
      credSubmitButton: "Add account",
      credSuccess: "Added {{account}} as a standalone account",
      credError: "Failed to add the account",

      existingTitle: "Current accounts",
      colEnabled: "Status",
      enabledOn: "Enabled",
      enabledOff: "Disabled (frozen)",
      enableButton: "Enable",
      disableButton: "Disable",
      enableDialogTitle: "Enable this account?",
      disableDialogTitle: "Disable this account?",
      enableDialogBody: "{{account}} will be swept and managed normally again.",
      disableDialogBody:
        "{{account}} will stop being swept — all memberships and service periods stay FROZEN as-is. " +
        "This does not delete any data; press Enable any time to resume managing it.",
      enableSuccess: "Enabled {{account}}",
      disableSuccess: "Disabled {{account}}",
      // rev 7 (21/7): removes the account from the pool entirely — see RemovePoolAccountDialog.tsx.
      removeButton: "Remove",
      removeDialogTitle: "Remove this account?",
      removeDialogBody:
        "{{account}} will stop being swept. Its observed Zoom membership rows are removed; customer " +
        "service periods and the audit history for this account are KEPT. This cannot be undone from here.",
      removeSuccess:
        "Removed {{account}} from the Online Channel ({{members}} observed member(s) cleared)",
      removeError: "Failed to remove the account",
      existingEmptyTitle: "No accounts yet",
      existingEmptyBody: "Search and add an account above to get started",
      standalone: {
        manageButton: "Manage",
        title: "{{name}} — standalone account",
        testTitle: "Test the Zoom credential",
        testHint:
          "Re-check the stored Server-to-Server credential against Zoom now (a token + one read). " +
          "Use it if you rotated the secret, or if the sweep started failing for this account.",
        testButton: "Test connection",
        testOk: "Verified {{when}}",
        acctInfoTitle: "Account type & seats",
        acctInfoHint:
          "A standalone account's Zoom app can't read its own plan or purchased seat count, so enter them by hand. The Accounts and Dashboard screens use these instead of showing '—'.",
        acctTypeLabel: "Account type",
        acctTypeNotSet: "Not set",
        seatsLabel: "Seats",
        seatsInvalid: "Seats must be a whole number greater than 0",
        // Distinct accessible names (aria-label) on top of the visible "Save"/"Clear" text — this
        // section sits right above one with its own Save/Clear buttons, and getByRole("button",
        // {name}) needs to tell the two pairs apart.
        acctInfoSaveLabel: "Save account type and seats",
        acctInfoClear: "Clear",
        acctInfoClearLabel: "Clear account type and seats",
        subPeriodTitle: "Zoom subscription period",
        subPeriodHint:
          "A standalone account's Zoom app can't read its own subscription, so enter the start and end dates by hand (once per renewal). The dashboard's 'selling past Zoom' check uses it.",
        subEndClear: "Clear",
      },

      operatorsTitle: "Online Channel operator access",
      operatorsSubtitle:
        "Owner is always included and can't be edited here; Owner and Admin can grant or revoke Admin/Viewer access",
      operatorsEmptyTitle: "No operators yet",
      operatorsEmptyBody:
        "Search for a portal user below to grant access to the Online Channel module",
      colOperatorEmail: "Email",
      colRole: "Role",
      colGrantedBy: "Granted by",
      colGrantedAt: "Granted at",
      ownerBadge: "Owner",
      candidateOwnerNote: "Already has full access as Owner",
      candidateAlreadyOperator: "Already an operator",
      userSearchLabel: "Search a portal user by email",
      userSearchPlaceholder: "name@1-to-all.com",
      userSearchHint: "Type at least 2 characters",
      grantSuccess: "Granted access to {{email}}",
      revokeButton: "Revoke",
      revokeDialogTitle: "Revoke operator access?",
      revokeDialogBody: "{{email}} will no longer be able to access the Online Channel module.",
      revokeSuccess: "Revoked access for {{email}}",

      // "Change role" modal (30/7) — re-POSTs the same operator row with a new role (POST /operators is
      // an upsert). Hidden on the Owner row (no underlying row) and on the signed-in staffer's own row
      // (the server rejects a self-change with 422).
      changeRoleButton: "Change role",
      changeRoleModalTitle: "Change role — {{email}}",
      changeRoleCurrentRoleLabel: "Current role",
      changeRoleUnchangedHint: "Choose a different role to enable Save",
      changeRoleSubmitButton: "Save",
      changeRoleSuccess: "Updated the role for {{email}}",

      // "Add User" modal (corrected flow, 21/7) — replaces the old always-visible search+Select section.
      addUserButton: "Add User",
      addUserModalTitle: "Add a user",
      addUserRoleStepTitle: "Choose a role",
      addUserRoleAdminDesc: "Everything an Owner can do, except changing Owner access.",
      // 30/7 gate removed Viewer's dashboard access — the old copy ("…dashboard, accounts, and users")
      // promised a tab this role can no longer open.
      addUserRoleViewerDesc: "Read-only — accounts and users.",
      addUserSubmitButton: "Add",
    },

    accounts: {
      title: "Accounts",
      subtitle: "All configured accounts, with usage figures from Zoom",
      colName: "Account name",
      colNumber: "Account no.",
      colAccountType: "Account type",
      colSeats: "Seats",
      colSubscriptionStart: "Subscription start",
      colSubscriptionEnd: "Subscription end",
      // rev 9 (21/7): renamed from "Licensed" — this counts membership rows assigned OUT of the pool
      // (user_type=2), which read as a duplicate of the Pool Users License column under the old name.
      colAssignedMembers: "Assigned",
      // rev 10 (22/7): "Members" is gone (see PoolAccountsPage's own banner) — this is its replacement,
      // Seats minus Assigned. Can be negative (an oversold pool) — the cell warn-tones that case.
      colUnassigned: "Unassigned",
      // rev 9 (21/7): Assigned / Seats as an integer percent — "—" when Seats is null/0.
      colUtilization: "Utilization",
      emptyTitle: "No accounts yet",
      emptyBody: "Go to Settings to add the first account",
      // rev 8 — "Sync now" (owner/admin only, beside the "Last Update" chip).
      syncNowButton: "Sync now",
      // rev 17c (23/7, "Sync now" feedback, option A) — the button now tracks the enqueued run to a real
      // observed completion (useRunPoolSweep, features/pool/hooks.ts) instead of a single "started" toast.
      syncRunning: "Syncing…",
      syncComplete: "Sync complete",
      syncPaused: "In the pause window (02:00–08:00) — this round will be skipped",
      syncTimeout: "Sync hasn't finished yet — check the Last Update badge again later",
      syncAlreadyRunning: "A sync is already running",
      autoSyncNote: "Auto-syncs every 5 minutes",
    },

    users: {
      title: "Users",
      subtitle: "Search customers and manage their service period",
      searchPlaceholder: "Search by email, account name, or account number",
      searchLabel: "Search users",
      colEmail: "Email",
      colAccount: "Account",
      colLicense: "License",
      colServiceStart: "Service start",
      colServiceEnd: "Service end",
      colRenew: "Renew",
      typeBasic: "Basic",
      typeLicensed: "Licensed",
      // 31/7 (คุณใหม่, real prod row jongsert+cr3@gmail.com) — replaces the old "Not found in any
      // account" muted span for a user with no current Zoom membership. account_id/user_type/role_id
      // arrive null together (one LEFT JOIN, see PoolUsersPage's own header banner), which was always
      // the "removed" case, not a distinct "never linked" one — so this is a rename-with-treatment-
      // change, not a new state. See PoolUsersPage's License-cell comment for the full story.
      removedBadge: "Removed",
      removedTooltip: "No longer found in any Online Channel account",
      renewYes: "Renewing",
      renewNo: "Not renewing",
      // r10 22/7: "Undecided" → "Not decided" everywhere the renew tri-state renders (principal feedback).
      renewUndecided: "Not decided",
      // r10 22/7: the ONLY way to open PeriodModal now — row click is gone. Was "Set period"; renamed
      // "Manage" because the modal now does more than periods (renew decision travels with creation too).
      // Hidden for pool-Viewer, and for anyone who isn't a CURRENT Licensed member UNLESS their row still
      // carries an open period (see PoolUsersPage's own comment on this rule).
      manageButton: "Manage",
      // rev 9 (21/7): License filter (Select) above the table — default Licensed, reflected in ?license=.
      filterLicenseLabel: "License filter",
      filterAllOption: "All",
      // r12 22/7 (principal feedback, prod screenshots): a client-side narrowing of "All" — see
      // hooks.ts's poolUserNeedsReview for the exact condition (open period, Zoom side isn't Licensed).
      filterNeedsReviewOption: "Needs review",
      needsReviewBadge: "Should be licensed",
      needsReviewTooltip:
        "In an open service period, but Zoom still shows Basic — the customer has already paid but isn't licensed yet. Check urgently.",
      emptyTitle: "No users found",
      emptyBody: "Try a different search term",
      filteredByAccount: "Filtered to {{account}}",
      clearAccountFilter: "Clear filter",

      // rev 17b — Manage-every-row + multi-select bulk actions (rev17b-addendum.md).
      unlinkOnly: {
        dialogTitle: "Remove from account — {{email}}",
        dialogBody:
          "This removes {{email}} from their current Zoom account immediately (unlink), without waiting for the sweep. Continue?",
        confirmButton: "Remove from account",
      },
      selectAllLabel: "Select all on this page",
      selectRowLabel: "Select {{email}}",
      // rev 17c (23/7, "รกเกินไป"): the always-visible badge is gone — this is now a hover tooltip on
      // the disabled checkbox itself (PoolUsersPage's own select-column cell). Pinned verbatim wording.
      ownerAdminTooltip: "Owner/Admin cannot be selected",
      openPeriodTooltip:
        "Has an open service period — manage this user individually via the Manage button.",
      standaloneWarning:
        "This standalone account doesn't have write access enabled yet — the system will safely skip it.",

      bulk: {
        selectedCount: "{{count}} selected",
        clearSelection: "Clear selection",
        licenseRemoveLabel: "Unassign license",
        licenseRemoveSubtitle: "User stays on the account — becomes Basic",
        unlinkLabel: "Remove from account",
        unlinkSubtitle: "Removed from this Zoom account immediately (unlink)",
        dryRunHeadline: "Test mode (dry-run) — nothing will actually change on Zoom",
        abort409:
          "Sweep data isn't fresh enough yet — the system pauses 02:00–08:00. Try again after 08:00, or press Sync now first.",
        partialFailureNote: "Some may have already gone through — check the ",
        confirmTitleLicenseRemove: "Unassign license for {{count}} users now?",
        confirmBodyLicenseRemove:
          "This removes the license from each selected user immediately (they stay on the account as Basic), without waiting for the sweep. Users the safety guards stop will be listed as skipped, not changed.",
        confirmTitleUnlink: "Remove {{count}} users from their account now?",
        confirmBodyUnlink:
          "This removes each selected user from their current Zoom account immediately, without waiting for the sweep. Users the safety guards stop will be listed as skipped, not removed.",
        emailListLabel: "Selected users ({{count}})",
        confirmButtonLicenseRemove: "Unassign license",
        confirmButtonUnlink: "Remove from account",
        // R1 typed-count gate (LIVE only) — the operator retypes the exact selected count to unlock confirm.
        typeCountLabel: "To confirm, type the number of selected users: {{count}}",
        typeCountPlaceholder: "Type {{count}}",
        progressLabel: "Processing {{from}}–{{to}} of {{total}}…",
        resultSummary: "{{ok}} succeeded · {{skipped}} skipped · {{failed}} failed",
        showDetailsButton: "Show per-user results",
        hideDetailsButton: "Hide per-user results",
      },

      // The CONSTRUCTIVE third bulk action (BulkSetPeriodDialog) — internal-operator plain register,
      // present tense, no softening. F5 convention: ONE term for the action ("service period" /
      // "Set period") reused verbatim across bar label / dialog title / confirm button / results.
      bulkSetPeriod: {
        barLabel: "Set period",
        barSubtitle: "Create a service period for the selected users",
        confirmTitle: "Set a service period for {{count}} users?",
        confirmBody:
          "This creates one service period — with the dates and renew decision below — for every selected user.",
        dateRangeSummary: "{{count}} users · {{start}} – {{end}}",
        arming: {
          undecided:
            "At the end date the system automatically removes each user's license (no unlink). This is the default when renew is left undecided.",
          renewYes:
            "Marked as renewing — no automatic license removal; these users enter the renewal-reminder path near the end date.",
          expiryLicenseOnly: "At the end date the system removes the license only (no unlink).",
          expiryGrace: "At the end date the system waits {{days}} grace days, then unlinks.",
          expiryDisassociate: "At the end date the system unlinks these users immediately.",
        },
        confirmButton: "Set period",
        progressLabel: "Setting period {{current}} of {{total}}…",
        partialFailureNote:
          "The server stopped responding — some periods above were already created. Re-running is safe: users that already have a period are skipped.",
        createdBadge: "Created",
        noneCreatedBadge: "Nothing created",
      },
    },

    period: {
      // r10 22/7: ONE generic title for both states (was newTitle/"Set service period" vs.
      // editTitle/"Manage service period") — the no-open-period state is a one-shot form that sets the
      // renew decision too now, not just dates, so "period" undersold it in either state.
      title: "Manage — {{email}}",
      // rev 6 R6: the old Status column (active/pending/inactive on Zoom's side) moved here as a
      // read-only info row — never an editable field. r12 22/7: relabelled + pool-specific value copy
      // (ZOOM_STATUS_LABEL_KEY in PeriodModal.tsx) — this is the customer's ZOOM account status, not our
      // own service-period status, and the two were easy to conflate (the kotchaphat "pending" case).
      startLabel: "Start date",
      endLabel: "End date",
      // r12 22/7 (principal feedback, prod screenshot): reworded — the old copy read as if it changed
      saveButton: "Save service period",
      createSuccess: "Set the service period for {{email}}",
      updateSuccess: "Service period updated",
      createConflict: "{{email}} already has an open service period",

      renewSectionTitle: "Renew decision",
      renewSectionHint:
        'Defaults to "Not decided" — leaving it unset means the job only removes the license at expiry (no unlink).',
      renewYesLabel: "Renewing (Yes)",
      renewNoLabel: "Not renewing (No)",
      renewUndecidedLabel: "Not decided",

      renewNewDatesTitle: "New service period",
      renewNewDatesHint:
        'Choosing "Renewing" requires the new period\'s dates before you can save.',
      renewSubmit: "Confirm renewal",
      renewSuccess: "Renewed {{email}} — closed the old period and opened the new one.",

      // 30/7 — the planned next round, entered WITH the Yes (one save). Its start is stated, not
      // asked for: it is always the day after this period ends.
      nextRoundTitle: "Next service period",
      nextRoundHint:
        "Starts {{date}}, the day after this period ends. The system switches over on that date — until then this screen keeps showing the current period.",
      nextEndLabel: "Next period ends",
      nextRoundPlanned: "Next: {{start}} → {{end}}",

      expiryActionLabel: "At expiry",
      actionLicenseOnly: "Remove license only (no unlink)",
      actionGrace: "Grace period before unlink",
      actionDisassociate: "Unlink immediately",
      graceDaysLabel: "Grace days",
      graceDaysHint: "Must be greater than 0",
      saveDecisionButton: "Save decision",
      decisionSuccess: "Renew decision saved",

      closeTitle: "Close service period",
      closeReasonLabel: "Reason for closing",
      closeReasonPlaceholder: "State a reason — required",
      closeSubmit: "Close period",
      closeSuccess: "Closed the service period for {{email}}",

      historyButton: "View history",
      historyTitle: "History — {{email}}",
      historyPeriodsTitle: "Service periods",
      historyAuditTitle: "Activity timeline",
      historyEmpty: "No history yet",
      historyPeriodClosed: "Closed — {{reason}}",
      historyPeriodOpen: "Current period",

      validation: {
        graceDaysRequired: "Enter the number of grace days",
        graceDaysPositive: "Grace days must be greater than 0",
        expiryActionRequired: "Choose what happens at expiry",
        endAfterStart: "End date must be after the start date",
        reasonRequired: "Enter a reason",
        nextEndRequired: "Enter the end date of the next service period",
        nextEndAfterStart: "The next period must end after it starts",
      },

      force: {
        sectionTitle: "Force an action now (skip the sweep)",
        sectionHint:
          "Runs immediately instead of waiting for the sweep's own cadence — confirm before using it.",
        licenseRemoveButton: "Force-remove license now",
        unlinkButton: "Force-unlink now",
        licenseRemoveDialogTitle: "Force-remove the license now?",
        licenseRemoveDialogBody:
          "This removes {{email}}'s license immediately, without waiting for the sweep. Continue?",
        unlinkDialogTitle: "Force-unlink now?",
        unlinkDialogBody:
          "This unlinks {{email}} from the account immediately, without waiting for the sweep. Continue?",
        // R1 late ruling (rev17b addendum) — ONE shared body, used by BOTH BulkForceDialog and
        // UnlinkOnlyDialog whenever isLive !== true (off/dry-run/unknown): swaps out for the live-danger
        // sentence so a confirm ceremony never claims "immediately" while the system genuinely can't write.
        // Reworded rev 17c (23/7) — the UI's own Dry-run mode is gone (PoolSystemPage), so the old
        // "test mode (dry-run)" framing no longer matches what an operator can even select; this now
        // just says the system is off, which is what "isLive !== true" actually means here.
        testModeBody:
          "The license-removal system is still off — this button won't have any real effect.",
        // R1 fail-safe (adversarial review) — shown while isLive is UNKNOWN (system-state query still
        // loading or errored). Neither the live-danger body nor the "system is off" claim is honest yet,
        // so we render this neutral line AND keep the confirm button disabled: an unknown write mode must
        // never enable an un-gated write, and must never assert the system is off.
        checkingWriteMode: "Checking write mode…",
        resultTitle: "Result",
        dryRunBadge: "Dry run",
        liveBadge: "Ran for real",
        noWriteBadge: "Nothing was sent",
        mirrorPendingNote:
          "This user isn't in the daily data mirror yet (new users land after the ~07:05 sync). A force now will be safely skipped — try again after the morning sync.",
        noActions: "No actions were recorded",
        outcome: {
          skipMirrorNotReady:
            "Stopped — this user has no fresh row in the daily data mirror yet (new users appear after the ~07:05 morning sync). Try again after that.",
          okLicenseRemoved: "License removed — the user is now Basic",
          okUnlinked: "Unlinked — the user has been removed from this account",
          dryRunLicenseRemoved: "Dry run — license removal simulated, nothing was sent to Zoom",
          dryRunUnlinked: "Dry run — unlink simulated, nothing was sent to Zoom",
          alreadyBasic: "Already Basic — there was no license to remove",
          abort404: "Stopped — this user no longer exists on the account",
          abortGuard: "Stopped by a safety guard — nothing was sent to Zoom",
          abortGetFailed: "Stopped — could not read the user from Zoom; try again",
          failedWrite: "Zoom rejected the write — see the audit log",
          skipGuard: "Skipped by a safety guard — nothing was sent to Zoom",
          skipHasOpenPeriod:
            "Skipped — this user has an open service period; manage them individually via the Manage button.",
          // R1 late ruling — the write was accepted, but the read-back verification (immediate + a
          // ~3s retry) still couldn't confirm the state changed — the next sweep round will close it out.
          acceptedPendingSweep:
            "Zoom accepted the command — waiting for the sweep to confirm it landed",
          // Set-period-only (synthesized client-side by BulkSetPeriodDialog, not emitted by the force
          // pipeline) — see forceOutcome.ts's ok_created/failed_create.
          okCreated: "Service period created",
          failedCreate: "Couldn't create the service period",
        },
      },
    },

    intents: {
      title: "Pending intents",
      subtitle: "Open two-phase writes the sweep never got a chance to complete — usually a crash",
      colTs: "Time",
      colAction: "Action",
      colEmail: "Email",
      colTrigger: "Trigger",
      colStatus: "Status",
      actionLicenseRemove: "License remove",
      actionUserUnlink: "User unlink",
      staleWarning: "Over 10 minutes — crashed?",
      pendingLabel: "Pending",
      resolveButton: "Resolve",
      emptyTitle: "No open intents",
      emptyBody: "Every intent the sweep started has a matching completion write",
      openCountAria: "{{count}} open",

      resolveTitle: "Resolve intent — {{email}}",
      resolveExplanation:
        "We don't actually know what Zoom did — check it against the sweep before deciding",
      noteLabel: "Note (required)",
      notePlaceholder: "What did you check, and what did you find?",
      successButton: "Zoom actually did it (success)",
      failedButton: "Zoom did not do it (failed)",
      confirmSuccessTitle: "Confirm: Zoom actually did this?",
      confirmSuccessBody:
        "Records that {{email}}'s action was carried out for real — the sweep will not retry it.",
      confirmFailedTitle: "Confirm: Zoom did not do this?",
      confirmFailedBody:
        "Records that {{email}}'s action was NOT carried out — the sweep will retry it next run.",
      resolveSuccessToastTitle: "Intent resolved",

      validation: {
        noteRequired: "Enter a note",
        noteTooLong: "Note must be 500 characters or fewer",
      },
    },

    dashboard: {
      title: "Dashboard",
      // rev 16: covers both the new License overview (now the first thing on the page) and the
      // mismatch cards below it — the old subtitle only described the cards, which used to be
      // everything on this screen and no longer are.
      subtitle:
        "License usage across the Online Channel, and where our system and Zoom's reality disagree",
      // rev 16 (23/7, principal feedback): renamed from the pronoun-swap "Zoom has it · we don't" /
      // "We have it · Zoom doesn't" — read as a riddle at a glance. Same two cards, same server
      // fields (card1_license_without_period / card2_period_without_license), clearer titles only.
      card1Title: "Licensed without a sale",
      card1Subtitle:
        "Zoom shows this seat as licensed, but there's no open service period for it — a possible unbilled or free license.",
      card2Title: "Sold without a license",
      card2Subtitle:
        "An open service period exists, but Zoom doesn't show the customer as licensed yet — they've paid but can't use it.",
      colEmail: "Email",
      colAccount: "Account",
      viewInUsers: "View in Users",
      emptyTitle: "Nothing to show",
      card1EmptyBody: "No licenses without a service period right now",
      card2EmptyBody: "No service periods without a license right now",

      // rev 16 — the new Overview at the top of the page (client-side aggregation over
      // GET /pool/accounts; see poolAccountStats.ts).
      overview: {
        title: "License overview",
        subtitle:
          "Every configured account — how much license we own, how much is used, how much is left",
        tileAccounts: "Accounts",
        tileSeats: "Total Paid Licenses",
        tileAssigned: "Assigned",
        tileUnassigned: "Unassigned",
        tileUtilization: "Utilization",
        breakdownTitle: "By account type",
        colAccountType: "Account type",
        colAccounts: "Accounts",
        colSeats: "Paid Licenses",
        colAssigned: "Assigned",
        colUnassigned: "Unassigned",
        colUtilization: "Utilization",
        // A standalone account added before its Account type/Seats were filled in (Settings →
        // Manage) — its seats are excluded from the Seats/Unassigned/Utilization totals rather
        // than guessed at, same "—" honesty rule as everywhere else in this table.
        tierUnknown: "Not set",
        // rev 16 same-day fix (adversarial review): shown only when a "Not set" account has real
        // assigned members — that's what actually makes the Total tiles above a blended figure.
        blendedNote:
          "{{count}} assigned seat(s) belong to an account with no recorded Seats yet — Unassigned/Utilization above include them without counting their seats. Fill in Account type & Seats (Settings) for an exact total.",
        emptyTitle: "No accounts yet",
        emptyBody: "Go to Settings to add the first account",
      },
    },

    system: {
      title: "License-removal system control",
      subtitle:
        "Turn the Online Channel's automatic license-removal job on/off and switch its mode",

      stateOff: "Off",
      stateLive: "LIVE",

      explainOff: "Fully off — the system never touches a customer's license",
      explainLive: "Removes real customer licenses via Zoom — has real effect on customers",

      // rev 17c (23/7, คุณใหม่: "ถ้าจะเทส คือเทสจริงด้วย user test") — Dry-run mode is gone from the
      // UI; this is the only surviving trace, for a legacy dry_run report from the server (see
      // PoolSystemPage's own rev 17c banner).
      legacyDryRunNote: "Legacy state (dry-run) — pick a new mode",

      updatedBy: "Last changed by {{who}} at {{when}}",
      neverUpdated: "Never changed",
      settingsSourceNote: "Never configured — using the default",
      hardDisabledNote:
        "Locked off from the server (.env break-glass) — can only be changed on the server",

      switchToOff: "Switch to Off",
      switchToLive: "Switch to LIVE",

      confirmOffTitle: "Turn the license-removal system off?",
      confirmOffBody:
        "The system will immediately stop removing any customer's license, until turned back on.",

      liveDialogTitle: "Confirm arming LIVE mode",
      liveDialogWarning:
        "This mode removes real customer licenses via Zoom — a removal already made cannot be undone.",
      liveTypeToConfirmLabel: 'Type "LIVE" (all caps) to confirm',
      liveTypeToConfirmPlaceholder: "LIVE",
      liveConfirmButton: "Arm LIVE mode",

      switchSuccess: "Switched to {{state}}",
    },

    audit: {
      title: "Audit log",
      subtitle: "Every write and sweep observation across the Online Channel module",

      filterEmailLabel: "Customer email",
      filterEmailPlaceholder: "name@example.com",
      filterActorLabel: "Actor email",
      filterActorPlaceholder: "exact email…",
      filterActionLabel: "Action",
      allActions: "All",

      colTime: "Time",
      colActor: "Actor",
      colAction: "Action",
      colTarget: "Target",
      colTrigger: "Trigger",
      colDetail: "Detail",
      colActions: "Actions",
      moreLines: "+{{count}} more",
      // r1 critique (24/7): a row's ONLY way into the detail modal used to be onRowClick on the whole
      // <tr> (a mouse-only "secret" full-row button — a <tr> with tabIndex + click/keydown but no
      // role="button"/"link" is invisible to a screen reader as an interactive control). This is the
      // declared, keyboard-focusable control; onRowClick stays on the row purely as a convenience click
      // target on top of it.
      viewDetails: "View details",

      emptyTitle: "No audit entries",
      emptyBody: "No entries match these filters",

      detailModalTitle: "Audit entry #{{id}}",
      detailTargetEmail: "Target email",
      detailTargetEmailOld: "Previous email",
      detailAccount: "Account",
      detailPeriodId: "Period ID",
      detailIntentAt: "Intent written at",
      detailResult: "Result",
      pendingResult: "Pending",
      detailHttpStatus: "HTTP status",
      detailZoomCode: "Zoom code",
      detailCompletedAt: "Completed at",
      detailIp: "IP",
      detailRequestId: "Request ID",
      detailRawJson: "Raw details (JSON)",

      // The canonical action → label map (poolAuditFormat.ts's AUDIT_ACTION_LABEL_KEY) — see that file's
      // own banner for where this list comes from.
      actionPoolAdd: "Added an account",
      actionPoolEnable: "Enabled an account",
      actionPoolDisable: "Disabled an account",
      actionAccountRemove: "Removed an account",
      actionAccountSubscriptionPeriod: "Set the Zoom subscription period (standalone)",
      actionAccountManualInfo: "Set the account type / seats (standalone)",
      actionCredentialTest: "Tested the Zoom credential (standalone)",
      actionObservedConflict: "Sweep observed: same email in more than one account",
      actionSweepManualTrigger: "Triggered a sync manually",
      actionPeriodCreate: "Created a service period",
      actionPeriodUpdate: "Updated a service period",
      actionPeriodClose: "Closed a service period",
      actionRenewSet: "Set the renew decision",
      actionOperatorGrant: "Granted Online Channel access",
      actionOperatorRevoke: "Revoked Online Channel access",
      actionSystemStateChange: "Changed the license-removal system state",
      actionIntentResolve: "Resolved a pending intent",
      actionForceLicenseRemove: "Forced a license removal",
      actionForceUnlink: "Forced an unlink",
      actionForceBulk: "Forced a bulk action",
      actionLicenseRemove: "Removed a license (expiry job)",
      actionUserUnlink: "Unlinked a user (expiry job)",
      actionObservedMoved: "Sweep observed: moved account",
      actionObservedJoined: "Sweep observed: joined",
      actionObservedLeftEstate: "Sweep observed: left the estate",
      actionObservedLicenseRemoved: "Sweep observed: license removed",
      actionObservedLicenseGranted: "Sweep observed: license granted",
      actionObservedRenamed: "Sweep observed: email renamed",
      actionObservedRecreated: "Sweep observed: user recreated",
      actionObservedRenameConflict: "Sweep observed: rename conflict",
      actionObservedBlankEmail: "Sweep observed: blank email in roster",
      // 30/7 — the nightly (23:00) daily-snapshot job behind the Reports tab; actor "system".
      actionSnapshotDaily: "Daily snapshot recorded",
    },

    // Reports tab ("Online account history", 30/7) — a daily snapshot of the whole estate, mirroring
    // the Dashboard's own gate (`_manage` only) and its Overview math (poolAccountStats.ts) so a past
    // day's numbers read the same way today's do.
    reports: {
      title: "Online account history",
      subtitle: "Daily snapshots of the Online Channel estate — pick a day to see its own numbers",
      period: "Period",
      // Two charts on one shared day axis (principal 31/7): daily movement first — that's the
      // question this screen answers — with the absolute level underneath as context.
      deltaTitle: "Assigned licenses — daily change",
      deltaUnit: "Change",
      deltaNoBaseline:
        "“—” = no snapshot on the previous day to compare against (the first day of the range, or a night the snapshot was missed).",
      levelTitle: "Assigned licenses per day",
      levelUnit: "Assigned",
      colDate: "Date",
      colAccounts: "Accounts",
      // Matches the Dashboard overview's own wording for the same figure (pool.dashboard.overview.tileSeats).
      colSeats: "Total Paid Licenses",
      colAssigned: "Assigned",
      colUnassigned: "Unassigned",
      colUtilization: "Utilization",
      emptyTitle: "No snapshots yet",
      emptyBody: "Snapshots are recorded daily at 23:00 — the first one appears after tonight",

      backToList: "Back to Online account history",
      capturedAt: "Captured {{when}}",
      dayNotFoundTitle: "No snapshot for this date",
      dayNotFoundBody:
        "No daily snapshot was recorded for this date — it may be before the Reports tab existed, or the sweep skipped that night.",
      accountsTitle: "Accounts on this day",
      colAccount: "Account",
      colType: "Type",
    },
  },
} satisfies Copy<typeof th>;

export default en;
